// FAQ accordion — single-open behaviour with a JS-driven height animation.
// Pure-CSS accordions can't animate height:auto, so we measure scrollHeight
// on open, animate to that pixel value, then drop the inline height back to
// auto once the transition ends. That keeps the open panel resize-friendly:
// once it's auto, reflow handles viewport changes natively.

const reducedMotion = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Transition fallback — if transitionend doesn't fire (e.g. scrollHeight
// happens to equal current height), clean up after this many ms anyway.
const FALLBACK_MS = 600

function clearPending(panel) {
    if (panel._faqEnd) {
        panel.removeEventListener('transitionend', panel._faqEnd)
        panel._faqEnd = null
    }
    if (panel._faqTimer) {
        clearTimeout(panel._faqTimer)
        panel._faqTimer = null
    }
}

function afterTransition(panel, done) {
    clearPending(panel)
    const handler = (e) => {
        if (e.target !== panel || e.propertyName !== 'height') return
        clearPending(panel)
        done()
    }
    panel._faqEnd = handler
    panel.addEventListener('transitionend', handler)
    panel._faqTimer = setTimeout(() => {
        clearPending(panel)
        done()
    }, FALLBACK_MS)
}

function openPanel(panel, trigger) {
    if (trigger.getAttribute('aria-expanded') === 'true') return

    const wasHidden = panel.hidden
    trigger.setAttribute('aria-expanded', 'true')
    panel.hidden = false

    if (reducedMotion) {
        panel.style.height = ''
        return
    }

    // If we were fully closed, start from 0. If we're mid-close transition,
    // start from the current visual height so it reverses smoothly.
    const start = wasHidden ? 0 : panel.getBoundingClientRect().height
    panel.style.height = start + 'px'
    // Force reflow so the next height change actually transitions.
    void panel.offsetHeight

    const target = panel.scrollHeight
    panel.style.height = target + 'px'

    afterTransition(panel, () => {
        if (trigger.getAttribute('aria-expanded') === 'true') {
            // Drop inline height so the panel re-flows on viewport resize.
            panel.style.height = ''
        }
    })
}

function closePanel(panel, trigger) {
    if (trigger.getAttribute('aria-expanded') !== 'true') return

    trigger.setAttribute('aria-expanded', 'false')

    if (reducedMotion) {
        panel.hidden = true
        panel.style.height = ''
        return
    }

    // Lock current visual height in px (it may have been auto) so the
    // transition has a concrete starting point.
    const current = panel.getBoundingClientRect().height
    panel.style.height = current + 'px'
    void panel.offsetHeight

    panel.style.height = '0px'

    afterTransition(panel, () => {
        if (trigger.getAttribute('aria-expanded') !== 'true') {
            panel.hidden = true
            panel.style.height = ''
        }
    })
}

const faq = {
    init() {
        document.querySelectorAll('[data-faq]').forEach((root) => {
            root.addEventListener('click', (e) => {
                const trigger = e.target.closest('[data-faq-trigger]')
                if (!trigger || !root.contains(trigger)) return

                const item = trigger.closest('[data-faq-item]')
                if (!item) return

                const panel = item.querySelector('[data-faq-panel]')
                if (!panel) return

                const willOpen = trigger.getAttribute('aria-expanded') !== 'true'

                if (willOpen) {
                    openPanel(panel, trigger)
                } else {
                    closePanel(panel, trigger)
                }
            })
        })
    },
}

export default faq
